import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface Product {
  id: string;
  name: string;
  category: string;
  tagline: string;
  description: string;
  highlights: string[];
  image: string;
  icon: string;
  accent: string;
}

const products: Product[] = [
  {
    id: 'vpn',
    name: 'KROKO VPN',
    category: 'Security & Privacy',
    tagline: 'Encrypted. Private. Unrestricted.',
    description:
      'KROKO VPN delivers military-grade encryption and blazing-fast servers across dozens of global locations — all wrapped in a native iOS interface. Your browsing stays private, your data stays secure, and your connection stays fast.',
    highlights: [
      'AES-256 encryption on all connections',
      'Zero-log policy for full privacy',
      'One-tap connect with intelligent server selection',
      'Built-in DNS leak protection',
    ],
    image:
      'https://public.readdy.ai/ai/img_res/942211d5c1240309a89a5db434c80ee2.jpg',
    icon: 'ri-shield-keyhole-line',
    accent: '#C74B1C',
  },
  {
    id: 'cleaner',
    name: 'KROKO Cleaner',
    category: 'Performance & Optimization',
    tagline: 'Free Space. Boost Speed. Stay Fast.',
    description:
      'KROKO Cleaner intelligently scans your iOS device, identifies storage waste, and removes junk files to restore peak performance. From duplicate photos to cache buildup, our smart engine keeps your iPhone running like new.',
    highlights: [
      'Deep storage scan in seconds',
      'Smart duplicate photo detection',
      'Cache and junk file removal',
      'Real-time storage usage insights',
    ],
    image:
      'https://readdy.ai/api/search-image?query=premium%20iPhone%2015%20Pro%20smartphone%20product%20mockup%20displaying%20a%20device%20cleaner%20optimization%20app%20with%20circular%20storage%20progress%20gauge%20and%20clean%20performance%20metrics%2C%20light%20minimal%20UI%20design%2C%20clean%20off-white%20neutral%20background%2C%20professional%20product%20photography%20studio%20lighting%2C%20tech%20product%20showcase&width=600&height=760&seq=kroko-cleaner-product-2&orientation=portrait',
    icon: 'ri-smartphone-line',
    accent: '#2D6A4F',
  },
];

function ProductCard({ product, reversed }: { product: Product; reversed: boolean }) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`grid lg:grid-cols-2 gap-14 items-center ${
        reversed
          ? `animate-slide-right ${isVisible ? 'visible' : ''}`
          : `animate-slide-left ${isVisible ? 'visible' : ''}`
      }`}
    >
      {/* Image */}
      <div className={reversed ? 'lg:order-2' : ''}>
        <div className="relative w-full h-[480px] rounded-2xl overflow-hidden bg-[#F2F0EA] flex items-center justify-center">
          <img
            src={product.image}
            alt={`${product.name} iOS application screenshot`}
            className="h-full object-contain object-top"
          />
        </div>
      </div>

      {/* Text */}
      <div className={reversed ? 'lg:order-1' : ''}>
        <div
          className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full border"
          style={{ borderColor: product.accent, color: product.accent }}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${product.icon} text-sm`} />
          </div>
          <span className="text-xs font-semibold tracking-wider uppercase whitespace-nowrap">
            {product.category}
          </span>
        </div>

        <h3
          className="font-extrabold text-[#0D0D0D] mb-2"
          style={{ fontFamily: 'Syne, sans-serif', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}
        >
          {product.name}
        </h3>
        <p
          className="font-semibold mb-5"
          style={{ color: product.accent, fontFamily: 'Syne, sans-serif', fontSize: '1.05rem' }}
        >
          {product.tagline}
        </p>
        <p className="text-[#4A4A4A] text-base leading-relaxed mb-8">
          {product.description}
        </p>

        <ul className="flex flex-col gap-3">
          {product.highlights.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                <i className="ri-check-line text-sm" style={{ color: product.accent }} />
              </div>
              <span className="text-[#3A3A3A] text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function ProductsSection() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();

  return (
    <section id="products" className="py-28 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={titleRef}
          className={`text-center mb-20 animate-fade-up ${titleVisible ? 'visible' : ''}`}
        >
          <span className="text-[#C74B1C] text-sm font-semibold tracking-[0.2em] uppercase">
            Our Products
          </span>
          <h2
            className="mt-3 font-extrabold text-[#0D0D0D]"
            style={{ fontFamily: 'Syne, sans-serif', fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}
          >
            Built for iOS. Designed to Impress.
          </h2>
          <p className="mt-4 text-[#6B6B6B] text-base max-w-xl mx-auto leading-relaxed">
            Two powerful tools, one purpose — to make your iPhone more secure,
            more private, and more performant than ever.
          </p>
        </div>

        {/* Products */}
        <div className="flex flex-col gap-24">
          {products.map((product, idx) => (
            <ProductCard key={product.id} product={product} reversed={idx % 2 !== 0} />
          ))}
        </div>
      </div>
    </section>
  );
}
